'use client';
import React, { ReactNode } from 'react';
import { styled } from 'styled-components';
import BreadCrumb, { BreadcrumbItem } from '../molecules/BreadCrumb';
import TabSection, { TabSectionProps } from '../molecules/TabSection';
import TitlePc from '../molecules/TitlePc';
import { BREAKPOINT_PC, BREAKPOINT_SP } from '@/util/const';
import BackToTopButton from '../atoms/BackToTopButton';
import { zIndexMap } from '@/util/const';

export type TitleNavLayoutProps = TabSectionProps & {
  notification?: ReactNode;
  breadCrumbItems?: BreadcrumbItem[];
  title?: ReactNode;
  subTitle?: string;
  children?: ReactNode;
  alignItems?: 'center' | 'flex-start';
  zIndexGap?: '16px';
  zIndexFix?: string;
  navIndex?: string;
  largerMobileTitle?: boolean;
  isProductDetail?: boolean;
};

export const TitleNavLayout: React.FC<TitleNavLayoutProps> = (props) => {
  return (
    <Container
      $alignItems={props.alignItems}
      $zIndexGap={props.zIndexGap}
      $zIndexFix={props.zIndexFix}
      $isProductDetail={props.isProductDetail}
    >
      <div className="children">
        {props.notification && <>{props.notification}</>}
        {props.breadCrumbItems && (
          <TitleWrapper>
            <BreadCrumb items={props.breadCrumbItems}></BreadCrumb>
          </TitleWrapper>
        )}
        {props.title && (
          <TitleWrapper>
            <TitlePc
              title={props.title}
              subtitle={props.subTitle}
              largerMobileTitle={props.largerMobileTitle}
            ></TitlePc>
          </TitleWrapper>
        )}
        <div className="tabChildren">
          {props.tabUrlList && (
            <NavWrapper $navIndex={props.navIndex}>
              <TabSection {...props}></TabSection>
            </NavWrapper>
          )}
          <div className="zIndexFix">{props.children}</div>
          <BackToTopWarpper>
            <BackToTopButton
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            ></BackToTopButton>
          </BackToTopWarpper>
        </div>
      </div>
    </Container>
  );
};
const NavWrapper = styled.div<{
  $navIndex: string | undefined;
}>`
  @media (max-width: ${BREAKPOINT_SP}) {
    width: calc(100vw);
    position: sticky;
    top: 0px;
    z-index: ${(props) => props.$navIndex ?? '1000'};
  }
  @media (min-width: ${BREAKPOINT_PC}) {
    width: calc(100vw - 20px);
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: ${BREAKPOINT_SP}) {
    width: calc(100vw - 40px);
  }
  @media (min-width: ${BREAKPOINT_PC}) {
    width: 100%;
  }
`;

const Container = styled.div<{
  $alignItems: string | undefined;
  $zIndexGap: string | undefined;
  $zIndexFix: string | undefined;
  $isProductDetail?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;

  > div.children {
    @media (min-width: ${BREAKPOINT_PC}) {
      max-width: 720px;
      width: 720px;
    }

    @media (min-width: 992px) {
      max-width: 960px;
      width: 960px;
    }
    @media (min-width: 1200px) {
      max-width: 1140px;
      width: 1140px;
    }

    gap: 24px;
    display: flex;
    padding: 50px 0px;
    flex-direction: column;
    align-items: ${(props) => props.$alignItems ?? 'center'};

    .tabChildren {
      width: 100%;
      gap: 24px;
      @media (max-width: ${BREAKPOINT_SP}) {
        gap: 0;
      }
      display: flex;
      flex-direction: column;
      align-items: ${(props) => props.$alignItems ?? 'center'};
      .zIndexFix {
        width: 100%;
        gap: 24px;
        display: flex;
        flex-direction: column;
        align-items: ${(props) => props.$alignItems ?? 'center'};
        @media (max-width: ${BREAKPOINT_SP}) {
          width: ${(props) =>
            props.$isProductDetail ? '100%' : 'calc(100% - 32px)'};
          gap: ${(props) => props.$zIndexGap || '24px'};
        }
      }
    }
    @media (max-width: ${BREAKPOINT_SP}) {
      width: calc(100vw);
      gap: 12px;
      background-color: var(--background-primary);
      .tabChildren {
        background-color: var(--background-primary);
        .zIndexFix {
          position: relative;
          z-index: ${(props) => props.$zIndexFix || '1'};
        }
      }
    }
  }
`;

const BackToTopWarpper = styled.div`
  @media (max-width: ${BREAKPOINT_SP}) {
    display: block;
    float: right;
    z-index: ${zIndexMap.header};
  }
  @media (min-width: ${BREAKPOINT_PC}) {
    display: none;
  }
`;
